import PasswordGate from '../components/PasswordGate';
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getInvoices, createInvoice, updateInvoice, deleteInvoice, downloadInvoicePDF, getClients, getServices } from '../services/api';
import api from '../services/api';

const STATUSES = ['Draft','Unpaid','Paid','Overdue','Cancelled'];
const BADGE = { Draft:'badge-muted', Unpaid:'badge-warn', Paid:'badge-green', Overdue:'badge-danger', Cancelled:'badge-danger' };
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const YEARS = ['2024','2025','2026','2027','2028'];
const DELETE_PASSWORD = 'Tek@2026!!';

export default function InvoicesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('All');
  const [monthFilter, setMonthFilter]   = useState('');
  const [yearFilter, setYearFilter]       = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [showForm, setShowForm]         = useState(false);
  const [editInv, setEditInv]           = useState(null);
  const [expensesRaw, setExpensesRaw]   = useState([]);
  const [form, setForm] = useState({ client_id:'', service_id:'', issue_date:'', due_date:'', tax_rate:0, notes:'', items:[{description:'',quantity:1,unit_price:''}] });
  const { data: services=[] } = useQuery({ queryKey:['services'], queryFn:getServices });
  const [serviceFilter, setServiceFilter] = useState('');
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  useEffect(()=>{
    if (monthFilter && yearFilter) {
      api.get('/expenses',{params:{month:monthFilter,year:yearFilter}})
        .then(r=>setExpensesRaw(r.data?.data||[]))
        .catch(()=>setExpensesRaw([]));
    } else {
      setExpensesRaw([]);
    }
  }, [monthFilter, yearFilter]);

  const { data:invData, isLoading } = useQuery({
    queryKey:['invoices',statusFilter,monthFilter,yearFilter,serviceFilter],
    queryFn:()=>getInvoices({
      status:statusFilter==='All'?undefined:statusFilter,
      month:monthFilter||undefined,
      year:yearFilter||undefined
    }).then(r=>r.data),
  });
  const { data:clientData } = useQuery({
    queryKey:['clients-list'],
    queryFn:()=>getClients({limit:200}).then(r=>r.data),
  });

  const createMut = useMutation({
    mutationFn: createInvoice,
    onSuccess:()=>{ qc.invalidateQueries(['invoices']); setShowForm(false); resetForm(); toast.success('Invoice created'); },
    onError: e=>toast.error(e.response?.data?.message||'Error'),
  });
  const updateMut = useMutation({
    mutationFn:({id,data})=>updateInvoice(id,data),
    onSuccess:()=>{ qc.invalidateQueries(['invoices']); setEditInv(null); toast.success('Updated'); },
    onError: e=>toast.error(e.response?.data?.message||'Error'),
  });
  const cancelMut = useMutation({
    mutationFn:id=>api.delete('/invoices/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['invoices']); toast.success('Cancelled'); },
  });
  const deleteMut = useMutation({
    mutationFn:id=>api.delete('/invoices/'+id+'/delete'),
    onSuccess:()=>{ qc.invalidateQueries(['invoices']); toast.success('Deleted permanently'); },
    onError: e=>toast.error(e.response?.data?.message||'Error'),
  });

  const invoices = invData?.data || [];
  const clients  = clientData?.data || [];

  const subtotal = form.items.reduce((s,i)=>s+(parseFloat(i.unit_price||0)*parseFloat(i.quantity||0)),0);
  const taxAmt   = subtotal*(parseFloat(form.tax_rate||0)/100);
  const total    = subtotal+taxAmt;

  const addItem    = () => setForm(p=>({...p,items:[...p.items,{description:'',quantity:1,unit_price:''}]}));
  const removeItem = i => setForm(p=>({...p,items:p.items.filter((_,idx)=>idx!==i)}));
  const setItem    = (i,k,v) => setForm(p=>({...p,items:p.items.map((it,idx)=>idx===i?{...it,[k]:v}:it)}));
  const resetForm  = () => setForm({ client_id:'', service_id:'', issue_date:'', due_date:'', tax_rate:0, notes:'', items:[{description:'',quantity:1,unit_price:''}] });

  const handleSave = () => {
    if (!form.client_id||!form.issue_date||!form.due_date) return toast.error('Client and dates required');
    createMut.mutate({...form,tax_rate:parseFloat(form.tax_rate)||0,items:form.items.filter(i=>i.description).map(i=>({...i,quantity:parseFloat(i.quantity)||1,unit_price:parseFloat(i.unit_price)||0}))});
  };

  const handleDelete = (id,num) => {
    const pwd = window.prompt('Enter delete password to permanently delete '+num+':');
    if (pwd===null) return;
    if (pwd!==DELETE_PASSWORD) { toast.error('Incorrect password!'); return; }
    if (window.confirm('Permanently DELETE '+num+'?')) deleteMut.mutate(id);
  };

  const handlePDF = async(id,num) => {
    try{
      const res=await downloadInvoicePDF(id);
      const url=URL.createObjectURL(new Blob([res.data],{type:'application/pdf'}));
      const a=document.createElement('a');a.href=url;a.download=num+'.pdf';a.click();
      URL.revokeObjectURL(url);
    }catch{toast.error('Failed');}
  };

  const printTable = () => {
    const win=window.open('','_blank');
    const rows=filteredInvoices.map(inv=>'<tr><td>'+inv.invoice_number+'</td><td>'+(inv.client?.name||'--')+'</td><td>'+(inv.client?.company||'--')+'</td><td>'+(inv.service?.name_en||'—')+'</td><td>KWD '+parseFloat(inv.total||0).toFixed(3)+'</td><td>'+inv.status+'</td><td>'+inv.issue_date+'</td><td>'+inv.due_date+'</td></tr>').join('');
    const tot=filteredInvoices.reduce((s,i)=>s+parseFloat(i.total||0),0);
    const pd=filteredInvoices.filter(i=>i.status==='Paid').reduce((s,i)=>s+parseFloat(i.total||0),0);
    const up=filteredInvoices.filter(i=>['Unpaid','Overdue'].includes(i.status)).reduce((s,i)=>s+parseFloat(i.total||0),0);
    win.document.write('<!DOCTYPE html><html><head><title>Invoices Report</title><style>body{font-family:Arial,sans-serif;padding:30px;color:#111}.header{display:flex;justify-content:space-between;border-bottom:2px solid #00b368;padding-bottom:16px;margin-bottom:20px}.company{font-size:22px;font-weight:700;color:#00b368}.stats{display:flex;gap:20px;margin-bottom:20px}.stat{background:#f5f5f5;padding:12px 20px;border-radius:8px}.stat-label{font-size:11px;color:#888}.stat-val{font-size:18px;font-weight:700;margin-top:2px}table{width:100%;border-collapse:collapse;font-size:13px}th{background:#f5f5f5;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;border-bottom:2px solid #eee;color:#666}td{padding:10px 12px;border-bottom:1px solid #f0f0f0}.total-row{font-weight:700;background:#f9f9f9}.footer{margin-top:30px;text-align:center;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:12px}</style></head><body><img src="https://teknulugy.online/logo.svg" style="height:50px;display:block;margin:0 auto 10px auto"><div class="header"><div style="text-align:right"><div style="font-size:18px;font-weight:700">Invoices Report</div><div style="font-size:12px;color:#666">Generated: '+new Date().toLocaleDateString()+'</div></div></div><div class="stats"><div class="stat"><div class="stat-label">Total</div><div class="stat-val">KWD '+tot.toFixed(3)+'</div></div><div class="stat"><div class="stat-label">Collected</div><div class="stat-val" style="color:#166534">KWD '+pd.toFixed(3)+'</div></div><div class="stat"><div class="stat-label">Outstanding</div><div class="stat-val" style="color:#854d0e">KWD '+up.toFixed(3)+'</div></div></div><table><thead><tr><th>Invoice #</th><th>Client</th><th>Company</th><th>Amount</th><th>Service</th><th>Status</th><th>Issue Date</th><th>Due Date</th></tr></thead><tbody>'+rows+'</tbody><tfoot><tr class="total-row"><td colspan="3">TOTAL</td><td>KWD '+tot.toFixed(3)+'</td><td colspan="3"></td></tr></tfoot></table><div class="footer">Teknulugy IT Services - Ahmed Al Jaber Street, Baitak Tower, Floor 1, Office D - +965 55107000</div></body></html>');
    win.document.close();
    setTimeout(()=>win.print(),500);
  };

  const byCompany = companyFilter ? invoices.filter(i=>i.client?.company===companyFilter) : invoices;
  const filteredInvoices = serviceFilter ? byCompany.filter(i=>String(i.service_id)===String(serviceFilter)) : byCompany;
  const paid    = filteredInvoices.filter(i=>i.status==='Paid').reduce((s,i)=>s+parseFloat(i.total||0),0);
  const unpaid  = filteredInvoices.filter(i=>['Unpaid','Overdue'].includes(i.status)).reduce((s,i)=>s+parseFloat(i.total||0),0);
  const totalAll = filteredInvoices.reduce((s,i)=>s+parseFloat(i.total||0),0);
  const totalExpenses = expensesRaw.reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const monthlyProfit = paid - totalExpenses;

  return (
    <PasswordGate moduleName="invoices" moduleLabel="Invoices">
    <div>
      <div className="g4" style={{marginBottom:12}}>
        <div className="card-sm"><div className="stat-label">Total Invoices</div><div className="stat-val">{invoices.length}</div></div>
        <div className="card-sm"><div className="stat-label">Total Amount</div><div className="stat-val">KWD {totalAll.toFixed(3)}</div></div>
        <div className="card-sm"><div className="stat-label">Collected</div><div className="stat-val" style={{color:'var(--green)'}}>KWD {paid.toFixed(3)}</div></div>
        <div className="card-sm"><div className="stat-label">Outstanding</div><div className="stat-val" style={{color:'var(--warn)'}}>KWD {unpaid.toFixed(3)}</div></div>
      </div>

      {monthFilter && yearFilter && (
        <div className="g3" style={{marginBottom:20}}>
          <div className="card-sm" style={{borderLeft:'3px solid var(--green)'}}>
            <div className="stat-label">Collected This Month</div>
            <div className="stat-val" style={{color:'var(--green)'}}>KWD {paid.toFixed(3)}</div>
          </div>
          <div className="card-sm" style={{borderLeft:'3px solid var(--danger)'}}>
            <div className="stat-label">Expenses This Month</div>
            <div className="stat-val" style={{color:'var(--danger)'}}>KWD {totalExpenses.toFixed(3)}</div>
            <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>{expensesRaw.length} expense entries</div>
          </div>
          <div className="card-sm" style={{borderLeft:monthlyProfit>=0?'3px solid var(--green)':'3px solid var(--danger)',background:monthlyProfit>=0?'#f0fdf7':'#fff5f5'}}>
            <div className="stat-label">Net Profit This Month</div>
            <div className="stat-val" style={{color:monthlyProfit>=0?'var(--green)':'var(--danger)'}}>
              {monthlyProfit>=0?'+ ':'-'} KWD {Math.abs(monthlyProfit).toFixed(3)}
            </div>
            <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Collected - Expenses</div>
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <div className="tab-bar" style={{flexWrap:'wrap'}}>
          {['All',...STATUSES].map(s=><button key={s} className={"tab-btn"+(statusFilter===s?" active":"")} onClick={()=>setStatusFilter(s)}>{s}</button>)}
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',marginLeft:'auto'}}>
          <select className="form-select" style={{width:130}} value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}>
            <option value="">All Months</option>
            {MONTHS.map((m,i)=><option key={i+1} value={String(i+1)}>{m}</option>)}
          </select>
          <select className="form-select" style={{width:90}} value={yearFilter} onChange={e=>setYearFilter(e.target.value)}>
            <option value="">Year</option>
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          {(monthFilter||yearFilter)&&<button className="btn btn-outline btn-sm" onClick={()=>{setMonthFilter('');setYearFilter('');}}>Clear</button>}
          <select className="form-select" style={{width:160}} value={companyFilter} onChange={e=>setCompanyFilter(e.target.value)}>
            <option value="">All Companies</option>
            {[...new Set(invoices.map(i=>i.client?.company).filter(Boolean))].sort().map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          {companyFilter&&<button className="btn btn-outline btn-sm" onClick={()=>setCompanyFilter('')}>×</button>}
          <select className="form-select" style={{width:175}} value={serviceFilter} onChange={e=>setServiceFilter(e.target.value)}>
            <option value="">All Services</option>
            {services.map(s=><option key={s.id} value={s.id}>{s.name_en}</option>)}
          </select>
          {serviceFilter&&<button className="btn btn-outline btn-sm" onClick={()=>setServiceFilter('')}>×</button>}
          <button className="btn btn-outline" onClick={printTable} style={{display:'flex',alignItems:'center',gap:6}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print
          </button>
          <button className="btn btn-primary" onClick={()=>{setShowForm(v=>!v);setEditInv(null);}}>+ Create Invoice</button>
        </div>
      </div>

      {editInv&&(
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">Edit Invoice — {editInv.invoice_number}</div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={editInv.status} onChange={e=>setEditInv(p=>({...p,status:e.target.value}))}>
                {STATUSES.map(s=><option key={s}>{s}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Due Date</label>
              <input className="form-input" type="date" value={editInv.due_date||''} onChange={e=>setEditInv(p=>({...p,due_date:e.target.value}))}/></div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
              <input className="form-input" value={editInv.notes||''} onChange={e=>setEditInv(p=>({...p,notes:e.target.value}))} placeholder="Notes..."/></div>
            <div className="form-group"><label className="form-label">Service</label>
              <select className="form-select" value={editInv.service_id||''} onChange={e=>setEditInv(p=>({...p,service_id:e.target.value}))}>
                <option value="">-- No Service --</option>
                {services.map(s=><option key={s.id} value={s.id}>{s.name_en}</option>)}
              </select></div>
          </div>
          <div style={{display:'flex',gap:8,marginTop:8}}>
            <button className="btn btn-primary" onClick={()=>updateMut.mutate({id:editInv.id,data:{status:editInv.status,due_date:editInv.due_date,notes:editInv.notes,service_id:editInv.service_id||null}})}>Save Changes</button>
            <button className="btn btn-outline" onClick={()=>setEditInv(null)}>Cancel</button>
          </div>
        </div>
      )}

      {showForm&&(
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">New Invoice</div>
          <div className="form-grid" style={{marginBottom:16}}>
            <div className="form-group"><label className="form-label">Client *</label>
              <select className="form-select" value={form.client_id} onChange={e=>set('client_id',e.target.value)}>
                <option value="">Select...</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Tax Rate %</label>
              <input className="form-input" type="number" min="0" max="100" value={form.tax_rate} onChange={e=>set('tax_rate',e.target.value)} placeholder="0"/></div>
            <div className="form-group"><label className="form-label">Issue Date *</label>
              <input className="form-input" type="date" value={form.issue_date} onChange={e=>set('issue_date',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Due Date *</label>
              <input className="form-input" type="date" value={form.due_date} onChange={e=>set('due_date',e.target.value)}/></div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
              <input className="form-input" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Notes..."/></div>
            <div className="form-group"><label className="form-label">Service (Optional)</label>
              <select className="form-select" value={form.service_id} onChange={e=>set('service_id',e.target.value)}>
                <option value="">-- Select Service --</option>
                {services.map(s=><option key={s.id} value={s.id}>{s.name_en}</option>)}
              </select></div>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <div className="form-section-title" style={{margin:0}}>Line Items</div>
              <button className="btn btn-outline btn-sm" onClick={addItem}>+ Add Item</button>
            </div>
            <table className="tbl">
              <thead><tr><th style={{width:'50%'}}>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th><th></th></tr></thead>
              <tbody>{form.items.map((item,i)=>(
                <tr key={i}>
                  <td><input className="form-input" value={item.description} onChange={e=>setItem(i,'description',e.target.value)} placeholder="Service description"/></td>
                  <td><input className="form-input" type="number" min="0.01" value={item.quantity} onChange={e=>setItem(i,'quantity',e.target.value)} style={{width:70}}/></td>
                  <td><input className="form-input" type="number" min="0" value={item.unit_price} onChange={e=>setItem(i,'unit_price',e.target.value)} placeholder="0"/></td>
                  <td style={{fontWeight:500,whiteSpace:'nowrap'}}>KWD {(parseFloat(item.unit_price||0)*parseFloat(item.quantity||0)).toFixed(3)}</td>
                  <td><button className="btn btn-danger btn-sm" onClick={()=>removeItem(i)}>X</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
            <div style={{minWidth:240,fontSize:13}}>
              <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',color:'var(--muted)'}}><span>Subtotal</span><span>KWD {subtotal.toFixed(3)}</span></div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',color:'var(--muted)'}}><span>Tax ({form.tax_rate||0}%)</span><span>KWD {taxAmt.toFixed(3)}</span></div>
              <div style={{height:1,background:'var(--border)',margin:'6px 0'}}/>
              <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontWeight:600,fontSize:15}}><span>Total</span><span style={{color:'var(--green)'}}>KWD {total.toFixed(3)}</span></div>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary" onClick={handleSave}>Create Invoice</button>
            <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead>
              <tr><th>Invoice #</th><th>Client</th><th>Company</th><th>Amount</th><th>Service</th><th>Status</th><th>Issue Date</th><th>Due Date</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filteredInvoices.length===0&&<tr><td colSpan={8} className="empty-state">No invoices found</td></tr>}
              {filteredInvoices.map(inv=>(
                <tr key={inv.id}>
                  <td style={{fontFamily:'monospace',fontSize:12,color:'var(--muted)'}}>{inv.invoice_number}</td>
                  <td style={{fontWeight:500}}>{inv.client?.name||'--'}</td>
                  <td style={{color:'var(--muted)',fontSize:12}}>{inv.client?.company||'--'}</td>
                  <td style={{fontWeight:600}}>KWD {parseFloat(inv.total||0).toFixed(3)}</td>
                  <td style={{fontSize:12,color:'#555'}}>{inv.service?.name_en||'—'}</td><td><span className={"badge "+(BADGE[inv.status]||'badge-muted')}>{inv.status}</span></td>
                  <td style={{color:'var(--muted)',fontSize:12}}>{inv.issue_date}</td>
                  <td style={{color:'var(--muted)',fontSize:12}}>{inv.due_date}</td>
                  <td>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      <select className="form-select" style={{width:110,padding:'4px 8px',fontSize:11}} value={inv.status}
                        onChange={e=>updateMut.mutate({id:inv.id,data:{status:e.target.value}})}>
                        {STATUSES.map(s=><option key={s}>{s}</option>)}
                      </select>
                      <button className="btn btn-outline btn-sm" onClick={()=>setEditInv({...inv,service_id:inv.service_id||''})}>Edit</button>
                      <button className="btn btn-outline btn-sm" onClick={()=>handlePDF(inv.id,inv.invoice_number)}>PDF</button>
                      {inv.status!=='Cancelled'&&<button className="btn btn-outline btn-sm" style={{color:'var(--warn)',borderColor:'var(--warn)'}} onClick={()=>{if(window.confirm('Cancel?'))cancelMut.mutate(inv.id);}}>Cancel</button>}
                      <button className="btn btn-danger btn-sm" onClick={()=>handleDelete(inv.id,inv.invoice_number)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </PasswordGate>
  );
}
